/* global initSqlJs */
import React, {useEffect, useState, useMemo} from 'react';
import _ from 'lodash';
import Script from 'next/script';
import * as Sentry from '@sentry/react';

import {runSQL} from '../../api/api';
import {getDraftKey, getMostRecentDraft, sleep} from '../../util';

const DELAY_MS = 100;
const PLACEHOLDER_TEXT = '-- Write your query here';
const LAST_USED_LANGUAGE = 'last_used_language_sql';

const emptyCode = {
  slug: null,
  title: '',
  sqlCode: '',
  postgresCode: '',
};

const Languages = [{
  value: 'sqlite',
  label: 'SQLite',
  code: 'sqlCode',
  tests: 'sqlTests',
}, {
  value: 'postgres',
  label: 'PostgreSQL',
  code: 'postgresCode',
  tests: 'postgresTests',
}];

export default function SQLEngine({
  code = emptyCode,
  draftSlug = code.slug,
  initialLanguage,
  children,
  onEditCode,
  onRunCode,
  onRunTests,
  onPassTests,
  onChangeLanguage: onChangeLanguageProp,
  disableDrafts = false,
  enableAllLanguages = false,
}) {
  const languageOptions = useMemo(() => (enableAllLanguages
    ? Languages
    : Languages.filter((lang) => code[lang.code] || code[lang.tests])
  ), [code]);

  const initializeLanguage = () => {
    if (initialLanguage) return initialLanguage;

    const lastUsedLanguage = localStorage.getItem(LAST_USED_LANGUAGE);

    if (disableDrafts) {
      return languageOptions.find(({value}) => value === lastUsedLanguage)?.value
      || languageOptions[0]?.value;
    }

    const mostRecentDraft = getMostRecentDraft(draftSlug);

    // Previously, drafts were always stored under 'sql', not per dbType.
    // This ensure backwards compatibility with users old drafts.
    if (mostRecentDraft?.language === 'sql') mostRecentDraft.language = 'sqlite';

    return mostRecentDraft?.language
      || languageOptions.find(({value}) => value === lastUsedLanguage)?.value
      || languageOptions[0]?.value;
  };

  const initializeContent = () => {
    if (disableDrafts) return PLACEHOLDER_TEXT;

    const mostRecentDraft = getMostRecentDraft(draftSlug);
    return mostRecentDraft?.data || PLACEHOLDER_TEXT;
  };

  const [db, setDb] = useState(null);
  const [dbType, setDbType] = useState(initializeLanguage);
  const [content, setContent] = useState(initializeContent);
  const [consoleResults, setConsoleResults] = useState(null);
  const [isRunning, setRunning] = useState(false);
  const [isTesting, setTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const language = Languages.find(({value}) => value === dbType);
  const initialCode = code?.[language.code];
  const testCode = code?.[language.tests];

  async function setupDb() {
    try {
      // Only set up SQLite if dbType is SQLite, otherwise exec(initialCode) can break
      if (dbType !== 'sqlite') return;

      // initSqlJs is a global defined by the sql.js script below
      if (typeof initSqlJs === 'undefined') return;

      const SQL = await initSqlJs({locateFile: () => 'https://cdnjs.cloudflare.com/ajax/libs/sql.js/1.8.0/sql-wasm.wasm'});
      const newDB = new SQL.Database();
      newDB.exec(initialCode);
      setDb(newDB);
    } catch (err) {
      Sentry.captureException(err);
      setConsoleResults({error: err.message});
    }
  }

  const onChangeLanguage = (languageKey) => {
    onChangeLanguageProp?.(languageKey);
    setDbType(languageKey);
    localStorage.setItem(LAST_USED_LANGUAGE, languageKey);

    // Update content with either the relevant draft or the default code
    const newLanguage = languageOptions.find((lang) => lang.value === languageKey);

    if (disableDrafts) {
      setContent(PLACEHOLDER_TEXT);
    } else {
      const draftKey = getDraftKey(draftSlug);
      const localStorageDrafts = JSON.parse(localStorage.getItem(draftKey));
      setContent(localStorageDrafts?.[languageKey]?.data || PLACEHOLDER_TEXT);
    }
  };

  async function runSQLite(query) {
    if (!db) await setupDb();
    await sleep(DELAY_MS); // Artificial delay
    const res = db.exec(query);
    return res[0];
  }

  async function runSQLAsync(query) {
    const res = await runSQL({
      setupSql: initialCode,
      querySql: query,
      dbType,
    });

    if (res.error) {
      throw new Error(res.error);
    }

    return res.rows?.[0] ? {
      columns: Object.keys(res.rows[0]),
      values: res.rows.map((row) => Object.values(row)),
    } : null;
  }

  async function run() {
    setRunning(true);
    setTestResults(null);
    setConsoleResults(null);

    try {
      onRunCode?.({language: 'sql', dbType});

      const res = dbType === 'sqlite'
        ? await runSQLite(content)
        : await runSQLAsync(content);

      setConsoleResults({output: res});
      setRunning(false);

      return res;
    } catch (err) {
      setConsoleResults({error: err.message});
    }

    setRunning(false);

    return null;
  }

  async function test() {
    setTesting(true);
    setTestResults(null);
    setConsoleResults(null);

    try {
      onRunTests?.({language: 'sql', dbType});

      let actual;
      let expected;

      if (dbType === 'sqlite') {
        // Use SQLite (synchronous) for both user's code and test code
        actual = await runSQLite(content);
        expected = await runSQLite(testCode);
      } else {
        // Use asynchronous execution for both user's code and test code
        [actual, expected] = await Promise.all([
          runSQLAsync(content),
          runSQLAsync(testCode),
        ]);
      }

      const isEqual = _.isEqual(actual, expected);

      setConsoleResults({output: actual});
      setTestResults({passed: isEqual});
      setTesting(false);
      if (isEqual) onPassTests?.({language: 'sql', dbType});

      return {results: actual, passed: isEqual};
    } catch (err) {
      setConsoleResults({error: err.message});
    }

    setTesting(false);

    return null;
  }

  const onChange = (value) => {
    onEditCode?.({language: 'sql'});
    setContent(value);

    if (!disableDrafts) {
      const draftKey = getDraftKey(draftSlug);
      const localStorageDrafts = JSON.parse(localStorage.getItem(draftKey));
      localStorage.setItem(draftKey,
        JSON.stringify({
          ...localStorageDrafts,
          [dbType]: {
            time: Date.now(),
            data: value,
          },
        }));
    }
  };

  const onReset = () => onChange(PLACEHOLDER_TEXT);

  const onFakeSubmit = () => setRunning(true);

  const onFakeSubmitFinished = (newResults) => {
    setConsoleResults({output: newResults});
    setTestResults(null);
    setRunning(false);
  };

  const onFakeTest = () => setTesting(true);

  const onFakeTestFinished = (newTestResults) => {
    setConsoleResults({output: newTestResults?.results});
    setTestResults({passed: newTestResults?.passed});
    setTesting(false);
  };

  useEffect(() => {
    // If script is already loaded, we can set up database immediately
    setupDb();
  }, [code, dbType]);

  return (
    <>
      <Script
        src="https://cdnjs.cloudflare.com/ajax/libs/sql.js/1.8.0/sql-wasm.js"
        onLoad={setupDb}
      />
      {children({
        codeProblem: code,
        content,
        languageOptions,
        activeLanguage: dbType,
        isRunning,
        isTesting,
        hasTests: !!testCode,
        testCode,
        testResults,
        consoleResults,
        dirty: content !== PLACEHOLDER_TEXT,
        onChange,
        onSubmit: run,
        onTest: test,
        onChangeLanguage,
        onReset,
        onFakeSubmit,
        onFakeSubmitFinished,
        onFakeTest,
        onFakeTestFinished,
      })}
    </>
  );
}
